import React from "react";
import { useUiLabels } from "../context/LocaleContext";

interface CertificateCardProps {
  cardId: string;
  title: string;
  description: string;
  imgUrl: string;
  category: string;
  organization: string;
  date: string;
  credentialId?: string;
  verificationUrl?: string;
}

const certImageSrc = (imgUrl: string) => {
  const path = imgUrl.startsWith("/") ? imgUrl : `/${imgUrl}`;
  return `${process.env.PUBLIC_URL || ""}/assets${path}`;
};

const CertificateCard: React.FC<CertificateCardProps> = ({
  cardId,
  title,
  description,
  imgUrl,
  category,
  organization,
  date,
  verificationUrl,
}) => {
  const ui = useUiLabels();
  const src = certImageSrc(imgUrl);
  const dialogId = `cert-modal-${cardId}`;
  const { alt: altPrefix, altFullscreen, verify } = ui.certificateCard;

  const getOrgStyle = (org: string) => {
    switch (org.toLowerCase()) {
      case "cisco":
        return "bg-sky-500/15 text-sky-200 ring-sky-400/25";
      case "cambridge certification authority":
        return "bg-violet-500/15 text-violet-200 ring-violet-400/25";
      default:
        return "bg-white/[0.06] text-zinc-300 ring-white/[0.08]";
    }
  };

  const getCategoryStyle = (cat: string) => {
    const c = cat.toLowerCase();
    if (c === "cybersecurity") return "bg-rose-500/12 text-rose-200 ring-rose-400/20";
    if (c === "networking") return "bg-emerald-500/12 text-emerald-200 ring-emerald-400/20";
    if (c === "sviluppo web" || c === "web development") return "bg-amber-500/12 text-amber-200 ring-amber-400/20";
    if (c === "sistema operativo" || c === "operating system") return "bg-indigo-500/12 text-indigo-200 ring-indigo-400/20";
    return "bg-white/[0.06] text-zinc-300 ring-white/[0.08]";
  };

  return (
    <div className="group relative">
      <div className="bezel-outer">
        <div className="bezel-inner overflow-hidden transition-[box-shadow] duration-700 ease-fluid group-hover:shadow-[0_24px_80px_-40px_rgba(124,92,255,0.25)]">
          <div className="relative overflow-hidden bg-[#0a0a0c]">
            <div className="absolute right-4 top-4 z-10">
              <span
                className={`inline-flex rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-wide ring-1 ${getOrgStyle(organization)}`}
              >
                {organization}
              </span>
            </div>
            <img
              src={src}
              alt={`${altPrefix} ${title} — ${organization}`}
              className="h-56 w-full cursor-pointer object-cover transition-transform duration-700 ease-fluid group-hover:scale-[1.04]"
              onClick={() => {
                const modal = document.getElementById(dialogId) as HTMLDialogElement;
                if (modal) modal.showModal();
              }}
              loading="lazy"
            />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#08080a] via-transparent to-transparent opacity-60" />
          </div>

          <div className="p-6">
            <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
              <h3 className="text-lg font-bold tracking-tight text-white">{title}</h3>
              <span className="shrink-0 font-mono text-xs text-zinc-500">{date}</span>
            </div>

            <div className="mb-4">
              <span
                className={`inline-flex rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-wide ring-1 ${getCategoryStyle(category)}`}
              >
                {category}
              </span>
            </div>

            <p className="mb-4 text-sm leading-relaxed text-zinc-400">{description}</p>

            {verificationUrl && (
              <div className="border-t border-white/[0.06] pt-4">
                <a
                  href={verificationUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm font-medium text-emerald-200/95 transition-colors duration-700 ease-fluid hover:text-white"
                >
                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" aria-hidden>
                    <path
                      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                      stroke="currentColor"
                      strokeWidth={1.25}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  {verify}
                </a>
              </div>
            )}
          </div>
        </div>
      </div>

      <dialog
        id={dialogId}
        className="fixed inset-0 z-[30] max-h-none max-w-none bg-transparent backdrop:bg-black/80"
        onClick={(e) => {
          const modal = document.getElementById(dialogId) as HTMLDialogElement;
          if (e.target === modal) modal.close();
        }}
      >
        <div className="mx-auto flex max-h-[90dvh] w-[min(96vw,56rem)] flex-col overflow-hidden rounded-[1.5rem] bg-[#0f0f12] ring-1 ring-white/[0.1] backdrop-blur-xl">
          <div className="flex items-center justify-between gap-4 border-b border-white/[0.08] px-5 py-4">
            <div>
              <h3 className="text-base font-bold text-white">{title}</h3>
              <p className="text-xs text-zinc-500">
                {organization} · {date}
              </p>
            </div>
            <button
              type="button"
              className="flex h-10 w-10 items-center justify-center rounded-full bg-white/[0.06] text-zinc-300 ring-1 ring-white/[0.08] transition-[transform,background-color] duration-700 ease-fluid hover:bg-white/[0.1] active:scale-95"
              onClick={() => {
                const modal = document.getElementById(dialogId) as HTMLDialogElement;
                modal.close();
              }}
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path d="M6 18L18 6M6 6l12 12" stroke="currentColor" strokeWidth={1.25} strokeLinecap="round" />
              </svg>
            </button>
          </div>
          <div className="overflow-y-auto p-4">
            <img
              src={src}
              alt={`${altFullscreen} ${title}`}
              className="mx-auto w-full max-h-[70dvh] rounded-xl object-contain ring-1 ring-white/[0.06]"
            />
          </div>
        </div>
      </dialog>
    </div>
  );
};

export default CertificateCard;
